import { Fragment } from "react"
import { useTranslation } from "react-i18next"

import type { Article } from "~/api/prismic/types"
import { InstagramLink, TwitterLink } from "~/components/SocialIconLink"
import type { Maybe } from "~/types/ts-utils"
import { usePrismicDateFormatter } from "~/utils/dates/useDateTimeFormatter"
import { withoutFalsyValues } from "~/utils/withoutFalsyValues"

export type Author = {
  name: Maybe<string>
  twitter: Maybe<string>
  instagram: Maybe<string>
  uid: string
}

export type AuthorDetailsProps = {
  authors: Maybe<Author>[]
  date: Article["first_publication_date"] | null
}

export const AuthorDetails: React.FC<AuthorDetailsProps> = ({
  authors,
  date,
}) => {
  const { t } = useTranslation("articlePage")
  const formatDate = usePrismicDateFormatter()

  const dateJSX = !!date && <time dateTime={date}>{formatDate(date)}</time>

  const authorList = withoutFalsyValues(authors)

  if (!authorList.length) return null

  if (authorList.length > 1) {
    return (
      <>
        {authorList.map(({ name, instagram, twitter, uid }, index) => {
          const twitterHandle = twitter?.trim()
          const instagramHandle = instagram?.trim()
          const isLastInList = index + 1 === authors.length

          return (
            <Fragment key={uid}>
              <span className="inline-flex items-center space-x-2">
                <span className="italic">{name}</span>
                {!!twitterHandle && (
                  <TwitterLink iconSize={16} username={twitterHandle} />
                )}
                {!!instagramHandle && (
                  <InstagramLink iconSize={16} username={instagramHandle} />
                )}
                {!isLastInList && ","}
              </span>
              {isLastInList ? <span>|</span> : null}
            </Fragment>
          )
        })}

        {dateJSX}
      </>
    )
  }

  const author = authorList[0]

  const authorName = author.name
  const twitterHandle = author.twitter?.trim()
  const instagramHandle = author.instagram?.trim()

  return (
    <>
      {!!authorName && (
        <span className="inline-flex items-center space-x-2">
          <span>{t("writtenBy", { name: authorName })}</span>
          {!!twitterHandle && (
            <TwitterLink iconSize={16} username={twitterHandle} />
          )}
          {!!instagramHandle && (
            <InstagramLink iconSize={16} username={instagramHandle} />
          )}
          <span>|</span>
        </span>
      )}

      {dateJSX}
    </>
  )
}
